// The client audio engine (client-audio spec R1/R5/R8-engine, design DD4/DD10; tasks.md
// 3.2). This module is the canonical home of the `AudioEngine` seam — the interface, its
// cue/bed value types, a `NullAudioEngine` no-op double (the production default before the
// first unlock and the inert double in no-`AudioContext` environments), and the real
// `WebAudioEngine`. `test/fake-audio-engine.ts` re-exports the seam types from here and adds
// the recording double the driver/component tests inject.
//
// The engine is deliberately imperative and side-effecting: the driver (group 5) calls it,
// it never returns reactive JSX and never touches `client/src/canvas/*` (owned by
// `pixel-art-pass`). Because audio is a pure presentation side-effect that is never fed back
// into the sim or the wire, it is EXEMPT from the `waggle-sim` determinism invariants and may
// key voices off `AudioContext` wall-clock time (spec R1).
//
// Testability: jsdom has no Web Audio API, so `WebAudioEngine` injects against a narrow
// `AudioContextLike` port (below) rather than constructing `new AudioContext()` itself. The
// stub `AudioContext` (test/stub-audio-context.ts) and a real `AudioContext` both satisfy the
// port, so the mixing graph, mute ramp, and idempotent teardown are all assertable in the
// `node` project — the audible waveform itself is the EXCUSED manual browser gate (task 8.4).

import { Season, Tier } from '../net/wire/messages';

// --- AudioEngine seam contract (canonical home; re-exported by test/fake-audio-engine.ts) --

/**
 * The MVP SFX cue catalogue (design D9 / spec R2): one named procedural voice per
 * store-observable core-loop edge, plus the UI-interaction blip fired at a component tap
 * callback. The driver decides *when* to fire each (identity-deduped, best-effort); the
 * engine only plays the named voice. `prompt-accepted`/`prompt-failed` are distinct voices;
 * `version` covers hot-swap and rollback; `season` is the season-turn sting (distinct from
 * the ambient-bed crossfade, spec R4); `activity` is the coarse scoreboard-swing cue.
 */
export type SfxCue =
  | 'own-elimination'
  | 'elimination'
  | 'prompt-generating'
  | 'prompt-repairing'
  | 'prompt-accepted'
  | 'prompt-failed'
  | 'version'
  | 'season'
  | 'activity'
  | 'ui-blip';

/** An ambient-bed request (design DD4 / spec R4): the synthesized bed crossfades with
 * `season` and shifts its filter/density with the confirmed zoom `tier`. */
export interface AmbientBed {
  season: Season;
  tier: Tier;
}

/**
 * The injectable audio seam (design D1/DD1, spec R1). A `WebAudioEngine` (real) and a
 * `NullAudioEngine` (no-op double) implement it; tests inject the recording fake. Kept
 * imperative and side-effecting — the driver calls it; it is never reactive JSX and never
 * touches the canvas.
 */
export interface AudioEngine {
  /** Fire a one-shot procedural SFX voice for `cue`. */
  sfx(cue: SfxCue): void;
  /** Set/crossfade the continuous ambient bed to `bed` (season + zoom tier). */
  ambient(bed: AmbientBed): void;
  /** Ramp the master gain to 0 (mute) / back to the current volume level (unmute); the
   * `AudioContext` stays Running so unmute needs no re-unlock gesture (spec R5). */
  setMuted(muted: boolean): void;
  /** Set the master-volume level (0..1, clamped) that scales the master gain — the DD8 notch
   * control (spec R5). Applied live while unmuted; stored (not audible) while muted, so it
   * takes effect on the next unmute. Additive to the seam — no group 1–5 injection site
   * calls it (review-2g, g3 note). */
  setMasterVolume(level: number): void;
  /** Await the `AudioContext` unlock (the first-gesture arming, spec R6). */
  resume(): Promise<void>;
  /** Stop every voice and fade out the bed; idempotent and safe to overlap (spec R8). */
  stopAll(): void;
  /** Stop, disconnect all nodes, and close the context; idempotent, partial-init-safe
   * (spec R8). */
  dispose(): void;
}

// --- injectable AudioContext port ---------------------------------------------------------
//
// A narrow structural port over exactly the Web Audio surface `WebAudioEngine` uses. Both a
// real `AudioContext` and the test stub are assignable to it, so no test ever constructs a
// real context (mirroring the `INERT_CONTROLLER`/`INERT_LOD` seam convention).

/** The three `AudioContext.state` values the engine distinguishes. */
export type AudioContextRuntimeState = 'suspended' | 'running' | 'closed';

/** The subset of `AudioParam` the engine schedules (gain envelopes + the mute ramp). */
export interface AudioParamLike {
  value: number;
  setValueAtTime(value: number, startTime: number): AudioParamLike;
  linearRampToValueAtTime(value: number, endTime: number): AudioParamLike;
  cancelScheduledValues(startTime: number): AudioParamLike;
}

/** The shared node surface: connect (chainable) + parameterless disconnect. */
export interface AudioNodeLike {
  connect(destination: AudioNodeLike): AudioNodeLike;
  disconnect(): void;
}

export interface GainNodeLike extends AudioNodeLike {
  readonly gain: AudioParamLike;
}

export interface OscillatorNodeLike extends AudioNodeLike {
  type: string;
  readonly frequency: AudioParamLike;
  onended: (() => void) | null;
  start(when?: number): void;
  stop(when?: number): void;
}

export interface AudioContextLike {
  readonly state: AudioContextRuntimeState;
  readonly currentTime: number;
  readonly destination: AudioNodeLike;
  createGain(): GainNodeLike;
  createOscillator(): OscillatorNodeLike;
  resume(): Promise<void>;
  close(): Promise<void>;
  addEventListener(type: 'statechange', listener: () => void): void;
  removeEventListener(type: 'statechange', listener: () => void): void;
}

// --- NullAudioEngine ----------------------------------------------------------------------

/**
 * The no-op double (spec R1). It is the production engine before the first unlock (the driver
 * runs its edge detection against it, firing only no-op calls) and the inert engine in
 * environments with no `AudioContext`. Every method is a safe no-op that constructs nothing.
 */
export class NullAudioEngine implements AudioEngine {
  sfx(): void {
    // no-op
  }
  ambient(): void {
    // no-op
  }
  setMuted(): void {
    // no-op
  }
  setMasterVolume(): void {
    // no-op
  }
  resume(): Promise<void> {
    return Promise.resolve();
  }
  stopAll(): void {
    // no-op
  }
  dispose(): void {
    // no-op
  }
}

// --- WebAudioEngine -----------------------------------------------------------------------

/** The fixed default master-gain level (also the adapter's default, kept in sync there). */
const DEFAULT_MASTER_VOLUME = 0.8;
/** Per-bus mix constants (design DD4: master + sfx/ambient split, per-bus levels fixed). */
const SFX_BUS_LEVEL = 0.9;
const AMBIENT_BUS_LEVEL = 0.55;
/** The bed's own level under the ambient bus (the crossfade target). */
const AMBIENT_BED_LEVEL = 0.6;
/** Ramp/envelope timings (seconds). Presentation tunables — audible quality is EXCUSED. */
const MUTE_RAMP_SECONDS = 0.02;
const SFX_ATTACK_SECONDS = 0.005;
const CROSSFADE_SECONDS = 1.5;

/** Per-cue base frequency (Hz) + duration (s). Presentation tunables (audible quality is the
 * EXCUSED manual gate); the automated contract only checks that voices are node-synthesized. */
const SFX_VOICES: Record<SfxCue, { freq: number; duration: number }> = {
  'own-elimination': { freq: 110, duration: 0.6 },
  elimination: { freq: 165, duration: 0.35 },
  'prompt-generating': { freq: 520, duration: 0.12 },
  'prompt-repairing': { freq: 440, duration: 0.12 },
  'prompt-accepted': { freq: 660, duration: 0.22 },
  'prompt-failed': { freq: 200, duration: 0.3 },
  version: { freq: 590, duration: 0.18 },
  season: { freq: 490, duration: 0.4 },
  activity: { freq: 330, duration: 0.15 },
  'ui-blip': { freq: 720, duration: 0.06 },
};

/** Drone partials per season (Hz). Winter is lowest/sparsest, summer highest — the crossfade
 * targets (spec R4). Tier chooses how many partials play (density). */
const SEASON_BED_PARTIALS: Record<Season, readonly number[]> = {
  [Season.Summer]: [98, 147, 196],
  [Season.Autumn]: [87, 130, 174],
  [Season.Winter]: [73, 110, 146],
};

/** A live voice — one or more oscillators plus the nodes it owns for teardown. */
interface Voice {
  readonly oscillators: readonly OscillatorNodeLike[];
  readonly nodes: readonly AudioNodeLike[];
  /** The bed's own gain node (ambient beds only), retained so a season/tier transition can
   * ramp it down as a true crossfade rather than hard-cutting it (spec R4). */
  readonly gain?: GainNodeLike;
  stopped: boolean;
}

export interface WebAudioEngineOptions {
  /** Initial master-gain level (0..1); non-finite/out-of-range values clamp to the default. */
  masterVolume?: number;
}

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_MASTER_VOLUME;
  }
  return Math.min(1, Math.max(0, value));
}

/**
 * The real Web Audio engine (design DD4). Builds `sfx` + `ambient` `GainNode` sub-buses into a
 * master `GainNode` → `destination`; synthesizes one-shot oscillator+envelope SFX voices on
 * the sfx bus and a looping drone bed on the ambient bus. Mute ramps the master gain to 0 with
 * the context left Running (spec R5). `stopAll()` and `dispose()` are both idempotent and
 * safe after a partial init (spec R8): every voice/node is stopped/disconnected at most once
 * (WeakSet-guarded) and the context is `close()`d at most once.
 */
export class WebAudioEngine implements AudioEngine {
  private readonly ctx: AudioContextLike;
  /** The current master-volume level (0..1) the master gain rides at when unmuted. Mutable so
   * the DD8 notch (`setMasterVolume`) can scale it live; `setMuted(false)` restores to it. */
  private masterVolume: number;
  /** Whether the master gain is currently muted (target 0). Tracked so `setMasterVolume` while
   * muted only stores the level (does not un-mute by ramping the master gain up). */
  private muted = false;
  /** The persistent bus gains (master, sfx, ambient) in creation order — whatever got built
   * before a partial-init failure. `dispose()` disconnects exactly these. */
  private readonly busNodes: AudioNodeLike[] = [];
  private master?: GainNodeLike;
  private sfxBus?: GainNodeLike;
  private ambientBus?: GainNodeLike;
  private readonly activeVoices = new Set<Voice>();
  private bedVoice?: Voice;
  private readonly stoppedOscillators = new WeakSet<OscillatorNodeLike>();
  private readonly disconnectedNodes = new WeakSet<AudioNodeLike>();
  private disposed = false;
  /** Set if graph construction failed part-way — the engine stays inert but disposable
   * (a constructor throw would deny the caller a handle to dispose the partial context). */
  readonly initError?: unknown;

  constructor(ctx: AudioContextLike, options: WebAudioEngineOptions = {}) {
    this.ctx = ctx;
    this.masterVolume = clampVolume(options.masterVolume ?? DEFAULT_MASTER_VOLUME);
    const now = ctx.currentTime;
    try {
      const master = this.createBusGain();
      master.gain.setValueAtTime(this.masterVolume, now);
      const sfxBus = this.createBusGain();
      sfxBus.gain.setValueAtTime(SFX_BUS_LEVEL, now);
      const ambientBus = this.createBusGain();
      ambientBus.gain.setValueAtTime(AMBIENT_BUS_LEVEL, now);
      sfxBus.connect(master);
      ambientBus.connect(master);
      master.connect(ctx.destination);
      this.master = master;
      this.sfxBus = sfxBus;
      this.ambientBus = ambientBus;
    } catch (err) {
      // Keep the partially-built bus nodes tracked (already pushed to `busNodes`) so
      // `dispose()` can still tear them down; the engine is inert from here.
      this.initError = err;
    }
  }

  sfx(cue: SfxCue): void {
    const sfxBus = this.sfxBus;
    if (this.disposed || sfxBus === undefined) {
      return;
    }
    const spec = SFX_VOICES[cue];
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(spec.freq, now);
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(1, now + SFX_ATTACK_SECONDS);
    env.gain.linearRampToValueAtTime(0, now + spec.duration);
    osc.connect(env);
    env.connect(sfxBus);
    const voice: Voice = { oscillators: [osc], nodes: [osc, env], stopped: false };
    this.activeVoices.add(voice);
    osc.onended = () => this.stopVoice(voice);
    osc.start(now);
    // Schedule the natural end; the guard makes this the voice's single stop.
    this.stopOscillator(osc, now + spec.duration);
  }

  ambient(bed: AmbientBed): void {
    const ambientBus = this.ambientBus;
    if (this.disposed || ambientBus === undefined) {
      return;
    }
    const now = this.ctx.currentTime;
    const previous = this.bedVoice;
    const partials = SEASON_BED_PARTIALS[bed.season];
    // Zoom tier shifts density: close-in plays the full partial stack, wide plays fewer.
    const density = bed.tier === Tier.Close ? partials.length : Math.max(1, partials.length - 1);

    const bedGain = this.ctx.createGain();
    bedGain.gain.setValueAtTime(0, now);
    bedGain.gain.linearRampToValueAtTime(AMBIENT_BED_LEVEL, now + CROSSFADE_SECONDS);
    bedGain.connect(ambientBus);

    const oscillators: OscillatorNodeLike[] = [];
    const nodes: AudioNodeLike[] = [bedGain];
    for (let i = 0; i < density; i += 1) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(partials[i], now);
      osc.connect(bedGain);
      osc.start(now);
      oscillators.push(osc);
      nodes.push(osc);
    }
    const voice: Voice = { oscillators, nodes, gain: bedGain, stopped: false };
    for (const osc of oscillators) {
      osc.onended = () => this.stopVoice(voice);
    }
    this.activeVoices.add(voice);
    this.bedVoice = voice;

    // Crossfade (spec R4, implementation-review F2): ramp the previous bed's gain DOWN to 0 over
    // CROSSFADE_SECONDS — equal-and-opposite to the new bed's fade-in above — and schedule its
    // oscillators to stop after that ramp, so old and new beds overlap during the transition
    // rather than the old one hard-cutting to silence. The previous voice's own `onended` wiring
    // disconnects its nodes when the scheduled stop fires; a hard `stopVoice()` (stopAll/dispose)
    // during the fade stays safe (idempotent).
    if (previous !== undefined && !previous.stopped) {
      this.crossfadeOut(previous, now);
    }
  }

  /** Retire a bed by crossfade: ramp its gain to 0 over CROSSFADE_SECONDS and schedule its
   * oscillators to stop after the ramp (their `onended` disconnects the nodes). Falls back to a
   * hard stop for a voice with no gain handle. Idempotent-safe against a later `stopVoice`. */
  private crossfadeOut(voice: Voice, now: number): void {
    if (voice.stopped) {
      return;
    }
    const gain = voice.gain;
    if (gain === undefined) {
      this.stopVoice(voice);
      return;
    }
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + CROSSFADE_SECONDS);
    for (const osc of voice.oscillators) {
      this.stopOscillator(osc, now + CROSSFADE_SECONDS);
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    const master = this.master;
    if (this.disposed || master === undefined) {
      return;
    }
    const target = muted ? 0 : this.masterVolume;
    this.rampMaster(target);
  }

  setMasterVolume(level: number): void {
    this.masterVolume = clampVolume(level);
    // While muted the master gain stays at 0 (the new level takes effect on the next unmute);
    // while unmuted the new level is applied live (the DD8 notch scaling the master gain, R5).
    if (this.disposed || this.master === undefined || this.muted) {
      return;
    }
    this.rampMaster(this.masterVolume);
  }

  /** Ramp the master gain from its current value to `target` over the mute-ramp window. */
  private rampMaster(target: number): void {
    const master = this.master;
    if (master === undefined) {
      return;
    }
    const now = this.ctx.currentTime;
    const gain = master.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(gain.value, now);
    gain.linearRampToValueAtTime(target, now + MUTE_RAMP_SECONDS);
  }

  resume(): Promise<void> {
    if (this.disposed) {
      return Promise.reject(new Error('WebAudioEngine: resume() after dispose()'));
    }
    return this.ctx.resume();
  }

  stopAll(): void {
    // Snapshot: `stopVoice` mutates `activeVoices`. Each voice's guard makes repeat/overlap safe.
    for (const voice of [...this.activeVoices]) {
      this.stopVoice(voice);
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.stopAll();
    for (const node of this.busNodes) {
      this.disconnectNode(node);
    }
    try {
      void this.ctx.close();
    } catch {
      // A context already closing/closed must not throw teardown.
    }
  }

  /** Create a persistent bus gain, tracked in `busNodes` before any connect so a mid-build
   * failure still leaves it disposable. */
  private createBusGain(): GainNodeLike {
    const gain = this.ctx.createGain();
    this.busNodes.push(gain);
    return gain;
  }

  /** Stop a voice at most once: stop its oscillators, disconnect its nodes, drop it from the
   * active set. Re-entrant-safe (an oscillator's `onended` calls back into this). */
  private stopVoice(voice: Voice): void {
    if (voice.stopped) {
      return;
    }
    voice.stopped = true;
    for (const osc of voice.oscillators) {
      this.stopOscillator(osc);
    }
    for (const node of voice.nodes) {
      this.disconnectNode(node);
    }
    this.activeVoices.delete(voice);
  }

  /** Stop an oscillator at most once (WeakSet-guarded), swallowing a runtime that already
   * stopped it. `when` schedules a future stop for self-ending one-shots. */
  private stopOscillator(osc: OscillatorNodeLike, when?: number): void {
    if (this.stoppedOscillators.has(osc)) {
      return;
    }
    this.stoppedOscillators.add(osc);
    try {
      osc.stop(when);
    } catch {
      // Already stopped by the runtime — idempotent by contract.
    }
  }

  /** Disconnect a node at most once (WeakSet-guarded). */
  private disconnectNode(node: AudioNodeLike): void {
    if (this.disconnectedNodes.has(node)) {
      return;
    }
    this.disconnectedNodes.add(node);
    try {
      node.disconnect();
    } catch {
      // Never connected / already disconnected — safe.
    }
  }
}
